import { useState, useEffect } from 'react';
import { History, Pencil, AlertTriangle } from 'lucide-react';
import { getTransferHistory, updateAdmissionDetails, type TransferRecord } from '@/lib/ipdApi';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={value ? 'text-sm text-foreground' : 'text-sm text-muted-foreground'}>{value || '—'}</dd>
    </div>
  );
}

const SECTION_TITLE = 'text-[15px] font-semibold text-foreground mb-2';

function TransferHistorySection({ admissionId, bedId }: { admissionId: string; bedId?: string }) {
  const [transfers, setTransfers] = useState<TransferRecord[]>([]);

  useEffect(() => {
    getTransferHistory(admissionId).then(data => setTransfers(data ?? [])).catch(console.error);
  }, [admissionId, bedId]);

  if (transfers.length === 0) return null;

  return (
    <div>
      <h3 className={`${SECTION_TITLE} flex items-center gap-1.5`}>
        <History className="w-3.5 h-3.5" /> Ward / bed transfers
      </h3>
      <div className="rounded-lg border border-border bg-card divide-y divide-border">
        {transfers.map(t => (
          <div key={t.id} className="px-4 py-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-xs text-muted-foreground tabular-nums">
              {format(new Date(t.transferred_at), 'dd MMM yyyy, h:mm a')}
            </span>
            <span>
              {t.from_ward_name} / {t.from_bed_number} → {t.to_ward_name} / {t.to_bed_number}
            </span>
            {t.notes && <span className="text-xs text-muted-foreground">— {t.notes}</span>}
            <span className="text-xs text-muted-foreground">by {t.transferred_by_name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Edit details ──────────────────────────────────────────────────────────────

const FIELDS = ['admission_reason', 'attendant_name', 'attendant_relation', 'attendant_phone', 'mlc_number',
  'referred_by', 'rate_plan', 'insurance_provider', 'notes'] as const;
type Field = typeof FIELDS[number];

function EditDetailsDialog({ admission, open, onClose, onSaved }: { admission: any; open: boolean; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<Record<Field, string>>({} as Record<Field, string>);
  const [isMlc, setIsMlc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(Object.fromEntries(FIELDS.map(f => [f, admission[f] ?? ''])) as Record<Field, string>);
    setIsMlc(!!admission.is_mlc);
    setError('');
  }, [open, admission]);

  const set = (f: Field) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(v => ({ ...v, [f]: e.target.value }));

  const save = async () => {
    if (saving) return;
    setError('');
    if (!form.admission_reason?.trim()) { setError('Give the reason for admission'); return; }
    setSaving(true);
    try {
      await updateAdmissionDetails(admission.id, {
        ...Object.fromEntries(FIELDS.map(f => [f, form[f]?.trim() || null])),
        admission_reason: form.admission_reason.trim(),
        is_mlc: isMlc,
      });
      onSaved(); onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const field = (f: Field, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div>
      <label htmlFor={`adm-${f}`} className="block text-[13px] font-semibold text-label mb-1">{label}</label>
      <Input id={`adm-${f}`} value={form[f] ?? ''} onChange={set(f)} className="h-9 text-sm" {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Admission details</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {field('admission_reason', 'Reason for admission / provisional diagnosis *', { maxLength: 500 })}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {field('attendant_name', 'Attendant', { maxLength: 100 })}
            {field('attendant_relation', 'Relation', { maxLength: 50 })}
            {field('attendant_phone', "Attendant's phone", { maxLength: 15, type: 'tel', inputMode: 'tel' })}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex items-center gap-2 text-sm min-h-9">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={isMlc} onChange={e => setIsMlc(e.target.checked)} />
              Medico-legal case (MLC)
            </label>
            {isMlc && <div className="w-56">{field('mlc_number', 'MLC no.', { maxLength: 50 })}</div>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {field('referred_by', 'Referred by', { maxLength: 100 })}
            {field('rate_plan', 'Rate plan', { maxLength: 100 })}
            {field('insurance_provider', 'Insurance / TPA', { maxLength: 100 })}
          </div>
          <div>
            <label htmlFor="adm-notes" className="block text-[13px] font-semibold text-label mb-1">Notes</label>
            <Textarea id="adm-notes" value={form.notes ?? ''} onChange={set('notes')} className="text-sm min-h-[60px]" />
          </div>
          {error && (
            <div role="alert" className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LEFT_LABEL: Record<string, string> = {
  discharged: 'Discharge', referred_out: 'Referral', absconded: 'Absconding', lama: 'LAMA', expired: 'Death',
};

export default function IPDOverviewTab({ admission, canWrite, onChanged }: { admission: any; canWrite?: boolean; onChanged?: () => void }) {
  const [editOpen, setEditOpen] = useState(false);
  if (!admission) return null;
  const attendant = [admission.attendant_name, admission.attendant_relation && `(${admission.attendant_relation})`, admission.attendant_phone]
    .filter(Boolean).join(' ');
  return (
    <div className="max-w-5xl space-y-6">
      {/* Blood group, allergies, dates and admitting doctor are in the header above; the rest of the admission here */}
      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className={SECTION_TITLE.replace(' mb-2', '')}>Admission details</h3>
          {canWrite && (
            <Button variant="ghost" size="sm" className="gap-1.5 h-8" onClick={() => setEditOpen(true)}>
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
          )}
        </div>
        <dl className="rounded-lg border border-border bg-card px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
          <div className="col-span-2 sm:col-span-3 lg:col-span-4">
            <InfoRow label="Reason for admission" value={admission.admission_reason} />
          </div>
          <InfoRow label="Attendant"          value={attendant} />
          <InfoRow label="Medico-legal case"  value={admission.is_mlc ? `Yes${admission.mlc_number ? ` · ${admission.mlc_number}` : ''}` : 'No'} />
          <InfoRow label="Consulting doctor"  value={admission.consulting_doctor_name} />
          <InfoRow label="Referred by"        value={admission.referred_by} />
          <InfoRow label="Department"         value={admission.department} />
          <InfoRow label="Rate plan"          value={admission.rate_plan} />
          <InfoRow label="Insurance provider" value={admission.insurance_provider} />
        </dl>
      </section>

      {admission.status !== 'admitted' && (admission.death_cause || admission.lama_signed_by || admission.discharge_billing_note) && (
        <section>
          <h3 className={SECTION_TITLE}>{LEFT_LABEL[admission.status] ?? 'Discharge'} record</h3>
          <dl className="rounded-lg border border-border bg-card px-4 py-3 grid grid-cols-1 gap-y-3 sm:grid-cols-2">
            {admission.death_cause && <InfoRow label="Cause of death" value={admission.death_cause} />}
            {admission.lama_signed_by && <InfoRow label="LAMA form signed by" value={admission.lama_signed_by} />}
            {admission.discharge_billing_note && <InfoRow label="Left with items open — reason" value={admission.discharge_billing_note} />}
          </dl>
        </section>
      )}

      {admission.notes && (
        <section>
          <h3 className={SECTION_TITLE}>Notes</h3>
          <p className="text-sm text-foreground whitespace-pre-wrap">{admission.notes}</p>
        </section>
      )}

      <TransferHistorySection admissionId={admission.id} bedId={admission.bed_id} />
      <EditDetailsDialog admission={admission} open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => onChanged?.()} />
    </div>
  );
}
